import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import Topbar from "./Topbar";
import Switch from "./automations/Switch";
import { dateTimeLong, ago } from "./ui";
import { AREA_OF_TYPE, AREA_LABEL } from "./navModel";

/**
 * AUTOMATIONS — the index.
 *
 * One row per automation, and the row is the switch plus the last seven days of
 * what it did. Built against the mockup (screen 1) with two departures, both
 * recorded in NOTES.md:
 *
 *  - The nav it sits in is the real one (Today / Pipeline / Follow-ups / Leads /
 *    Contacts / Approvals / Reports), not the mockup's Leads / Companies /
 *    Deals / Pipeline. There is no deals object — architecture decision 5.
 *  - There is a DRY RUN pill the mockup has no concept of, and it is the most
 *    important thing on the page. An automation that is Active and dry-running
 *    is not sending, and a page that let that be invisible would be lying about
 *    the one fact Gabriel most needs at a glance.
 *
 * THE PAGE COMPUTES ONE THING and it is the status pill, because the pill is a
 * rendering of `enabled` rather than a fact of its own — see statusOf. Every
 * number arrives from the server already counted, which is the rule the board,
 * Today and Follow-ups all follow (src/actions/model.js says why).
 */

/*
 * THE STATUS PILL IS DERIVED, WHICH IS WHY IT CANNOT DISAGREE WITH THE SWITCH.
 *
 * The mockup's annotation says "the status pill can say Active while the switch
 * is off only during a save race, so we don't let that happen: toggling writes
 * both." Deriving it removes the race rather than managing it: there is one
 * `enabled` and two renderings of it.
 *
 * Draft versus Paused is the distinction the mockup draws with its own rows —
 * "Stale lead nudge · Draft · Never" against "Post-call follow-up · Paused ·
 * Aug 29". So: off and never run is a draft, off and has run is paused.
 */
function statusOf(automation) {
  if (automation.enabled) return { key: "active", label: "Active" };
  if (automation.last_run) return { key: "paused", label: "Paused" };
  return { key: "draft", label: "Draft" };
}

function countLine(automations) {
  const active = automations.filter((a) => a.enabled).length;
  const drafts = automations.length - active;
  const parts = [];
  if (active) parts.push(`${active} active`);
  // "0 drafts" is noise; "no automations yet" is the empty state's job.
  if (drafts) parts.push(`${drafts} ${drafts === 1 ? "draft" : "drafts"}`);
  return parts.join(" · ");
}

/*
 * `area` narrows the list to one part of the menu — Inbound or Meetings — so an
 * automation is found beside the thing it serves. Without it (the old
 * /automations address) the page lists everything, as it always did.
 */
export default function AutomationsPage({ area = null }) {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState(null);
  const [types, setTypes] = useState([]);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api("/automations");
      const inArea = (type) => !area || AREA_OF_TYPE[type] === area;
      setAutomations(data.automations.filter((a) => inArea(a.type)));
      setTypes(data.types.filter((t) => inArea(t.type)));
    } catch (err) {
      setError(err.message);
    }
  }, [area]);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * The switch round-trips before the row changes. Optimism here would be the
   * wrong trade: the thing being toggled is whether a robot writes to
   * strangers, so the honest UI is one that waits for the database to agree.
   */
  const toggle = async (automation, next) => {
    setPending((p) => ({ ...p, [automation.id]: true }));
    try {
      const updated = await api(`/automations/${automation.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: next }),
      });
      setAutomations((list) => list.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
    } catch (err) {
      setError(err.message);
    } finally {
      setPending((p) => ({ ...p, [automation.id]: false }));
    }
  };

  if (error) {
    return (
      <>
        <Topbar title="Automations" measured />
        <div className="crm-auto crm-auto--index">
          <div className="crm-card crm-card--warning">{error}</div>
        </div>
      </>
    );
  }

  if (!automations) {
    return (
      <>
        <Topbar title="Automations" measured />
        <div className="crm-auto crm-auto--index">
          <p className="crm-hint">Loading…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar title="Automations" sub={[area && AREA_LABEL[area], countLine(automations)].filter(Boolean).join(" · ")} measured>
        <button type="button" className="crm-btn crm-btn--primary" onClick={() => setModalOpen(true)}>
          New automation
        </button>
      </Topbar>

      <div className="crm-auto crm-auto--index">
        {!automations.length ? (
          <div className="crm-card crm-auto-empty">
            <h3>Nothing automated yet</h3>
            <p className="crm-hint">
              {area === "meetings"
                ? "Reminders before a booked call, or new times after one nobody came to."
                : "The first one answers inbound leads that arrive when nobody is around, with three times from your calendar."}{" "}
              It starts as a draft, and in dry run, so you can read what it would have sent before it sends anything.
            </p>
            <button type="button" className="crm-btn crm-btn--primary" onClick={() => setModalOpen(true)}>
              New automation
            </button>
          </div>
        ) : (
          <div className="crm-auto-table crm-card">
            <div className="crm-auto-row crm-auto-row--head">
              <div>Automation</div>
              <div>Status</div>
              <div>Last run</div>
              <div className="crm-auto-stats-head">Last 7 days · sent / replied / booked</div>
              <div className="crm-auto-on">On</div>
            </div>

            {automations.map((automation) => {
              const status = statusOf(automation);
              const stats = automation.stats || {};
              return (
                <div
                  key={automation.id}
                  /*
                   * The whole row opens the record, on click — his standing
                   * preference, and the same behaviour as a Leads row. The
                   * switch stops propagation so arming an automation does not
                   * also navigate away from the list.
                   */
                  className={`crm-auto-row${automation.enabled ? "" : " is-quiet"}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/automations/${automation.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(`/automations/${automation.id}`);
                    }
                  }}
                >
                  <div className="crm-auto-name">
                    <b>{automation.name}</b>
                    <span className="crm-hint">{describe(automation, types)}</span>
                  </div>

                  <div className="crm-auto-status">
                    <span className={`crm-pill crm-pill--${status.key}`}>{status.label}</span>
                    {/*
                      THE MOST IMPORTANT THING ON THE PAGE. An Active automation
                      in dry run is not writing to anybody, and there is no way
                      to know that from the mockup's own vocabulary.
                    */}
                    {/*
                      Only on a row that is actually running. "Active · Dry run"
                      is the fact worth shouting; "Draft · Dry run" is two ways
                      of saying the same nothing, and it made all three rows
                      look equally live.
                    */}
                    {automation.enabled && automation.dry_run && (
                      <span className="crm-pill crm-pill--dryrun">Dry run</span>
                    )}
                    {!!automation.config_errors?.length && (
                      <span className="crm-pill crm-pill--broken" title={automation.config_errors.join(" ")}>
                        Needs fixing
                      </span>
                    )}
                  </div>

                  <div className="crm-auto-lastrun">
                    {automation.last_run ? (
                      <span title={dateTimeLong(automation.last_run.scored_at)}>{ago(automation.last_run.scored_at)}</span>
                    ) : (
                      <span className="crm-hint">Never</span>
                    )}
                  </div>

                  <div className="crm-auto-stats">
                    <Stat value={stats.sent} label="sent" />
                    <span className="crm-auto-stats__dot">·</span>
                    <Stat value={stats.answered} label="replied" />
                    <span className="crm-auto-stats__dot">·</span>
                    {/* Booked is blue. It is the number that pays. */}
                    <Stat value={stats.booked} label="booked" accent />
                  </div>

                  <div className="crm-auto-on">
                    <Switch
                      on={automation.enabled}
                      pending={!!pending[automation.id]}
                      label={`${automation.enabled ? "Switch off" : "Switch on"} ${automation.name}`}
                      onChange={(next) => toggle(automation, next)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <NewAutomationModal
          types={types}
          onClose={() => setModalOpen(false)}
          onCreated={(automation) => navigate(`/automations/${automation.id}`)}
        />
      )}
    </>
  );
}

/**
 * A dash rather than a 0 when nothing has run. The mockup draws it that way and
 * it is the right distinction: "0 sent" is a measurement, "—" is the absence of
 * one, and a draft has not measured anything.
 */
function Stat({ value, label, accent = false }) {
  const empty = value === null || value === undefined;
  return (
    <span className={`crm-auto-stat${accent ? " crm-auto-stat--accent" : ""}${empty ? " is-empty" : ""}`}>
      <b>{empty ? "—" : value}</b> {label}
    </span>
  );
}

function describe(automation, types) {
  const type = types.find((t) => t.type === automation.type);
  return type ? type.doc : automation.type;
}

/**
 * NEW AUTOMATION — two steps on one screen.
 *
 * The mockup's annotation: "Trigger then name. Everything else lives on the
 * detail page, so the modal never grows." That is a rule about where settings
 * go, and it is worth keeping — every knob this automation has is on a tab, so
 * the modal stays two fields however many knobs get added.
 *
 * IT CREATES A DRAFT, never an active automation. The server enforces it (the
 * column defaults say so) and the button says so.
 */
function NewAutomationModal({ types, onClose, onCreated }) {
  const available = types.filter((t) => t.available);
  const [type, setType] = useState(available[0]?.type || "");
  const [name, setName] = useState("After-hours inbound auto-reply");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const automation = await api("/automations", { method: "POST", body: JSON.stringify({ type, name }) });
      onCreated(automation);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="crm-modal__backdrop" onClick={onClose}>
      <form className="crm-modal crm-auto-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="crm-modal__head">
          <div>
            <h2>New automation</h2>
            <p className="crm-hint">Pick what kicks it off. You'll set the rules next.</p>
          </div>
          <button type="button" className="crm-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="crm-modal__body">
        <fieldset className="crm-auto-triggers">
          <legend className="crm-label">Trigger</legend>
          {types.map((option) => (
            <label
              key={option.type}
              className={`crm-auto-trigger${type === option.type ? " is-selected" : ""}${
                option.available ? "" : " is-unavailable"
              }`}
            >
              <input
                type="radio"
                name="trigger"
                value={option.type}
                checked={type === option.type}
                disabled={!option.available}
                onChange={() => setType(option.type)}
              />
              <b>{option.label}</b>
              <span className="crm-hint">{option.doc}</span>
              {/*
                The three that do not exist render disabled rather than being
                hidden. A picker that quietly has one option reads as a mistake,
                and the list is the roadmap.
              */}
              {!option.available && <span className="crm-auto-trigger__soon">Not built yet</span>}
            </label>
          ))}
        </fieldset>

        <label className="crm-field">
          <span className="crm-label">Name</span>
          <input
            className="crm-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </label>

        {error && <p className="crm-hint crm-hint--warn">{error}</p>}
        </div>

        <div className="crm-modal__foot">
          <button type="button" className="crm-btn crm-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="crm-btn crm-btn--dark" disabled={busy || !type || !name.trim()}>
            {busy ? "Creating…" : "Create draft"}
          </button>
        </div>
      </form>
    </div>
  );
}
